export type NarrationGenerationState =
  'queued' | 'running' | 'succeeded' | 'failed';

export interface NarrationGenerationRequestResource {
  requestId: string;
  state: NarrationGenerationState;
  statusUrl: string;
  errors?: string[];
  audio?: unknown;
}

export interface NarrationGenerationEnvelope {
  audioGenerationRequest?: NarrationGenerationRequestResource;
}

export class NarrationGenerationPollingError extends Error {
  constructor(
    public readonly code: string,
    public readonly errors: string[] = []
  ) {
    super(code);
  }
}

type PollOptions = {
  fetchStatus: (requestId: string) => Promise<NarrationGenerationEnvelope>;
  wait?: (signal?: AbortSignal) => Promise<void>;
  maxAttempts?: number;
  maxConsecutiveFailures?: number;
  signal?: AbortSignal;
};

const defaultWait = (signal?: AbortSignal) =>
  new Promise<void>(resolve => {
    const timer = window.setTimeout(resolve, 2000);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

const isTemporarilyUnavailable = (error: unknown) =>
  error instanceof NarrationGenerationPollingError &&
  error.code === 'status_unavailable';

// 330 polls x 2s outlasts the backend's 10-minute expiry, so the server decides when a request is lost.
// The generation keeps running (and is billed) while status checks fail, so brief outages must not abandon it.
export async function waitForGeneratedNarration(
  initial: NarrationGenerationEnvelope,
  {
    fetchStatus,
    wait = defaultWait,
    maxAttempts = 330,
    maxConsecutiveFailures = 5,
    signal
  }: PollOptions
): Promise<{ audio: unknown }> {
  let resource = initial.audioGenerationRequest;
  if (!resource?.requestId) {
    throw new NarrationGenerationPollingError('invalid_response');
  }

  let consecutiveFailures = 0;
  for (let attempt = 0; attempt <= maxAttempts; attempt += 1) {
    if (resource.state === 'failed') {
      throw new NarrationGenerationPollingError(
        'generation_failed',
        resource.errors ?? []
      );
    }
    if (resource.state === 'succeeded') {
      if (!resource.audio) {
        throw new NarrationGenerationPollingError('invalid_response');
      }
      return { audio: resource.audio };
    }
    if (attempt === maxAttempts) break;

    await wait(signal);
    if (signal?.aborted) {
      throw new NarrationGenerationPollingError('aborted');
    }

    let next: NarrationGenerationEnvelope;
    try {
      next = await fetchStatus(resource.requestId);
      consecutiveFailures = 0;
    } catch (error) {
      if (signal?.aborted) {
        throw new NarrationGenerationPollingError('aborted');
      }
      consecutiveFailures += 1;
      if (
        !isTemporarilyUnavailable(error) ||
        consecutiveFailures >= maxConsecutiveFailures
      ) {
        throw error;
      }
      continue;
    }
    resource = next.audioGenerationRequest;
    if (!resource?.requestId) {
      throw new NarrationGenerationPollingError('invalid_response');
    }
  }

  throw new NarrationGenerationPollingError('generation_timed_out');
}
